import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ArrowLeft, Download, ExternalLink } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssetDot } from '@/shared/components/AssetDot';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import type { DocumentDetail } from '../api/schemas';

const GENERIC_AUTHOR = 'OCBC Global Markets Research';

/** Back, what kind of report and when, the title, who wrote it, what it covers, and the PDF. */
export function ReportHeader({ doc, meta }: { doc: DocumentDetail; meta: string }) {
  const navigate = useNavigate();
  const cameFromApp = useLocation().key !== 'default';
  const authors = doc.analysts.filter((a) => a !== GENERIC_AUTHOR);

  return (
    <Stack sx={{ gap: 1.25, px: { xs: 2, md: 3 }, pt: 2, pb: 1.75, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.5 }}>
        <IconButton aria-label="Back" onClick={() => { void (cameFromApp ? navigate(-1) : navigate('/library')); }} sx={squareSx}>
          <ArrowLeft size={16} />
        </IconButton>
        <Stack sx={{ flexGrow: 1, minWidth: 0, gap: 0.25 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
            <AssetDot assetClass={doc.assetClass} />
            <Typography variant="caption" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>{doc.assetClass}</Typography>
            <Typography variant="caption" noWrap>· {meta}</Typography>
          </Stack>
          <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '1.25rem', md: '1.375rem' } }}>{doc.title}</Typography>
        </Stack>
        <Tooltip title="Download">
          <IconButton aria-label="Download PDF" component="a" href={doc.pdfUrl} download sx={squareSx}><Download size={16} /></IconButton>
        </Tooltip>
        <Button variant="outlined" size="small" startIcon={<ExternalLink size={14} />} href={doc.pdfUrl} target="_blank" rel="noreferrer" sx={{ flexShrink: 0, color: 'text.primary', borderColor: 'divider', display: { xs: 'none', sm: 'inline-flex' } }}>
          Open PDF
        </Button>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: { xs: 1, md: 1.5 }, flexWrap: 'wrap', pl: { md: 6 } }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Written by</Typography>
        {(authors.length ? authors : [GENERIC_AUTHOR]).map((a) => (
          <Stack key={a} direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
            <PersonAvatar name={a} size={24} />
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{a}</Typography>
          </Stack>
        ))}
        {doc.currencies.length ? <Box aria-hidden sx={{ width: '1px', height: 18, bgcolor: 'divider', mx: 0.5 }} /> : null}
        {doc.currencies.length ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>Covers</Typography> : null}
        {doc.currencies.slice(0, 5).map((c) => <Box key={c} component="span" sx={tagSx}>{c}</Box>)}
      </Stack>
    </Stack>
  );
}

const squareSx = { width: 32, height: 32, flexShrink: 0, border: 1, borderColor: 'divider', borderRadius: 1, color: 'text.secondary' } as const;

const tagSx = { px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: '0.6875rem', fontWeight: 500, bgcolor: 'surface.sunken' } as const;
